import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';

type ColorMode = 'light' | 'dark';
type ThemeSet = 'theme-one' | 'theme-two';

interface ThemeContextType {
  colorMode: ColorMode;
  themeSet: ThemeSet;
  toggleColorMode: () => void;
  setActiveThemeSet: (themeSet: ThemeSet) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  // Check for saved theme preferences
  const getInitialColorMode = (): ColorMode => {
    const savedColorMode = localStorage.getItem('colorMode') as ColorMode | null;
    
    if (savedColorMode && ['light', 'dark'].includes(savedColorMode)) {
      return savedColorMode as ColorMode;
    }
    
    // Default to dark mode
    return 'dark';
  };

  const getInitialThemeSet = (): ThemeSet => {
    // Always return theme-one, ignoring user preferences
    return 'theme-one';
  };

  const [colorMode, setColorMode] = useState<ColorMode>(getInitialColorMode);
  const [themeSet, setThemeSet] = useState<ThemeSet>(getInitialThemeSet);

  // Apply theme to document
  useEffect(() => {
    const root = window.document.documentElement;
    
    // Remove all theme classes
    root.classList.remove('light', 'dark', 'theme-one', 'theme-two');
    
    // Add current theme classes - always keep theme-one
    root.classList.add(colorMode, 'theme-one');
    
    // Save to localStorage only the color mode
    localStorage.setItem('colorMode', colorMode);

    // Update theme-color meta tag
    const themeColorMeta = document.querySelector('meta[name="theme-color"]');
    if (themeColorMeta) {
      themeColorMeta.setAttribute('content', colorMode === 'dark' ? '#09090B' : '#ffffff');
    }
  }, [colorMode]);

  const toggleColorMode = () => {
    setColorMode(prevMode => (prevMode === 'light' ? 'dark' : 'light'));
  };

  // Keep this function for backwards compatibility, but it does nothing now
  const setActiveThemeSet = () => {
    // Do nothing - we always use theme-one
  };

  const value = { 
    colorMode, 
    themeSet,
    toggleColorMode, 
    setActiveThemeSet 
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

// Custom hook to use the theme context
export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  
  return context;
};

// For backward compatibility with existing components
export const useThemeCompat = () => {
  const { colorMode, toggleColorMode } = useTheme();
  
  return {
    theme: colorMode,
    toggleTheme: toggleColorMode
  };
}; 